import React, { useState, useCallback, useRef, useMemo } from "react";

// Deterministic, non-crypto fallback key seed/counter used ONLY when window.crypto/msCrypto
// are unavailable (e.g. some test environments). This avoids Math.random so Sonar (S2245)
// does not flag a cryptographic weakness. Uniqueness within a session is sufficient because
// these IDs are React element keys only and never exposed externally or used for security.
const __pinKeySeed = Date.now();
let __pinKeyCounter = 0;
import PinInput from "./PinInput";

interface PinInputGroupProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  length?: number;
}

const PinInputGroup: React.FC<PinInputGroupProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  length = 8,
}) => {
  const [focusedIndex, setFocusedIndex] = useState<number>(0);

  // Stable keys for each position (avoid using array index directly as key)
  const keyRef = useRef<string[]>([]);
  // Secure random id generator (falls back to Math.random if crypto unavailable)
  const generateKey = () => {
    // Use two 32-bit values to construct a base36 string for entropy (~64 bits)
    try {
      if (typeof window !== "undefined") {
        // Support legacy IE (msCrypto) without unsafe non-null assertions
        const win = window as Window & { msCrypto?: Crypto };
        const cryptoObj: Crypto | undefined = window.crypto ?? win.msCrypto;
        if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
          const buf = new Uint32Array(2);
          cryptoObj.getRandomValues(buf);
          return (
            "pin-" +
            Array.from(buf)
              .map((n) => n.toString(36))
              .join("")
              .slice(0, 16)
          );
        }
      }
    } catch {
      /* ignore and fallback */
    }
    // Fallback: deterministic, session‑scoped incremental id (no security requirement)
    const id = (__pinKeySeed + __pinKeyCounter++).toString(36);
    return "pin-" + id.padEnd(14, "0").slice(0, 16);
  };

  if (keyRef.current.length !== length) {
    const prev = keyRef.current;
    keyRef.current = Array.from({ length }, (_, i) => prev[i] ?? generateKey());
  }

  // Always create an array of length 'length', filling with value's digits or empty strings; use nullish coalescing so '0' is preserved
  // Handle special encoding for empty positions
  const digits = useMemo(() => {
    if (value.includes("·")) {
      // Parse special encoding where · represents empty positions
      return value
        .split("")
        .map((char) => (char === "·" ? "" : char))
        .slice(0, length)
        .concat(Array(Math.max(0, length - value.length)).fill(""));
    }
    return Array.from({ length }, (_, i) => value[i] ?? "");
  }, [value, length]);

  const handleDigitChange = useCallback(
    (index: number, digit: string) => {
      const newDigits = Array.from({ length }, (_, i) => digits[i] ?? "");
      newDigits[index] = digit;
      // Preserve all positional placeholders (no trimming) so subsequent digits never shift left
      const encodedValue = newDigits.map((d) => (d === undefined || d === null || d === "" ? "·" : d)).join("");
      onChange(encodedValue);
      if (digit && index < length - 1) setFocusedIndex(index + 1);
    },
    [digits, length, onChange]
  );

  const backspaceTracker = useRef<{ index: number; time: number }>({
    index: -1,
    time: 0,
  });
  const encodeDigits = (arr: string[]) => arr.map((d) => (d === undefined || d === null || d === "" ? "·" : d)).join("");

  const handleBackspace = useCallback(
    (index: number) => {
      const now = Date.now();
      const isRapidRepeat =
        backspaceTracker.current.index === index &&
        now - backspaceTracker.current.time < 500;
      const newDigits = [...digits];
      if (newDigits[index]) {
        newDigits[index] = "";
        if (isRapidRepeat && index > 0) {
          newDigits[index - 1] = "";
          setFocusedIndex(index - 1);
        }
      } else if (index > 0) {
        newDigits[index - 1] = "";
        setFocusedIndex(index - 1);
      }
      onChange(encodeDigits(newDigits));
      backspaceTracker.current = { index, time: now };
    },
    [digits, onChange]
  );

  const handleArrowNav = useCallback(
    (index: number, key: string) => {
      if (key === "ArrowLeft" && index > 0) {
        setFocusedIndex(index - 1);
      } else if (key === "ArrowRight" && index < length - 1) {
        setFocusedIndex(index + 1);
      }
    },
    [length]
  );

  const handleKeyDown = useCallback(
    (index: number, key: string) => {
      if (key === "Backspace") {
        handleBackspace(index);
        return;
      }
      handleArrowNav(index, key);
    },
    [handleBackspace, handleArrowNav]
  );

  const handlePaste = useCallback(
    (index: number, pasted: string) => {
      const onlyDigits = pasted.replace(/\D/g, "");
      if (!onlyDigits) return;
      const newDigits = [...digits];
      for (let i = 0; i < onlyDigits.length && index + i < length; i++) {
        newDigits[index + i] = onlyDigits[i];
      }

      // Encode with position preservation
      // ReDoS-hardening: manual trailing middle-dot trim
      const encodedValue = encodeDigits(newDigits);
      onChange(encodedValue); // ensure parent value updates so inputs reflect pasted digits
      const next = Math.min(index + onlyDigits.length, length - 1);
      setFocusedIndex(next);
    },
    [digits, length, onChange]
  );

  return (
    <div className="flex gap-2 justify-center">
      {digits.map((digit, position) => (
        <PinInput
          key={keyRef.current[position]}
          value={digit}
          index={position}
          onChange={handleDigitChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={disabled}
          error={error}
          autoFocus={position === focusedIndex}
        />
      ))}
    </div>
  );
};

export default PinInputGroup;
